import React, { useState } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

export function BatchCreateInspectionModal({ siteId, tabs, onClose, onCreated }) {
  const [inspectionType, setInspectionType] = useState("monthly");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16));
  const [selectedKeys, setSelectedKeys] = useState(() => new Set(tabs.map((t) => t.key)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleTab = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedKeys(new Set(tabs.map((t) => t.key)));
  const selectNone = () => setSelectedKeys(new Set());

  const handleSave = async () => {
    if (selectedKeys.size === 0) {
      setError("Select at least one section.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      let created = 0;
      for (const tab of tabs) {
        if (!selectedKeys.has(tab.key)) continue;
        const siteArea = tab.kind === "area" ? tab.area : null;

        const res = await axios.post(`${API}/inspections/`, {
          site: siteId,
          site_area: siteArea ? siteArea.id : null,
          is_unsectioned_units: tab.kind === "pinned" && tab.areaType === "unit_group",
          inspection_type: inspectionType,
          date,
          inspector_name: "",
          assigned_to: null,
          status: "in_progress",
        });
        const inspectionId = res.data.id;

        const efsmsRes = siteArea
          ? await axios.get(`${API}/site-efsms/?area=${siteArea.id}`)
          : await axios.get(`${API}/site-efsms/?site=${siteId}`);
        let scopedEfsms;
        if (siteArea) {
          scopedEfsms = efsmsRes.data;
        } else if (tab.kind === "pinned" && tab.areaType === "unit_group") {
          // Unsectioned tenant units: assets live on units with no area of
          // their own, so cross-reference which units qualify.
          const unitsRes = await axios.get(`${API}/site-units/?site=${siteId}`);
          const unsectionedUnitIds = new Set(
            unitsRes.data.filter((u) => !u.area).map((u) => u.id)
          );
          scopedEfsms = efsmsRes.data.filter((e) => e.unit && unsectionedUnitIds.has(e.unit));
        } else {
          scopedEfsms = efsmsRes.data.filter((e) => !e.unit && !e.area);
        }
        const matchingEfsms = scopedEfsms.filter((e) => {
          // An EFSM with no schedule configured at all is always eligible,
          // rather than silently excluded from every inspection type forever.
          const unconfigured = !e.is_monthly && !e.is_bi_annual && !e.is_annual;
          if (inspectionType === "monthly") return e.is_monthly || unconfigured;
          if (inspectionType === "bi_annual") return e.is_bi_annual || unconfigured;
          if (inspectionType === "annual") return e.is_annual || unconfigured;
          return false;
        });
        const assetIds = matchingEfsms.flatMap((e) => (e.assets || []).map((a) => a.id));
        if (assetIds.length > 0) {
          await axios.post(`${API}/inspection-items/bulk_create/`, {
            inspection: inspectionId,
            asset_ids: assetIds,
          });
        }
        created += 1;
      }
      onCreated(created);
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <SimpleModal
      title="Set Up Separate Inspections"
      onClose={onClose}
      onSave={handleSave}
      saving={saving}
      error={error}
      wide
    >
      <div style={{ fontSize: "13px", color: colors.slate, marginBottom: "16px" }}>
        Pick which Asset Register sections should become their own separate,
        independently-assignable inspections. Assign technicians afterward
        from the Assign Work page.
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Inspection Type *</label>
        <div style={{ display: "flex", gap: "12px" }}>
          {[
            ["monthly", "Monthly"],
            ["bi_annual", "Bi-Annual"],
            ["annual", "Annual"],
          ].map(([val, label]) => (
            <div
              key={val}
              onClick={() => setInspectionType(val)}
              style={{
                flex: 1,
                padding: "12px",
                border: `2px solid ${inspectionType === val ? colors.activeBlue : colors.border}`,
                borderRadius: "8px",
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: inspectionType === val ? "#eff6ff" : "white",
              }}
            >
              <div style={{ fontWeight: "600", color: inspectionType === val ? colors.activeBlue : colors.text }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Inspection Date & Time *</label>
        <input
          style={{ ...inputStyle, maxWidth: "260px" }}
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <div style={fieldStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Sections to include</label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={selectAll} style={{ fontSize: "12px", color: colors.activeBlue, background: "none", border: "none", cursor: "pointer" }}>Select all</button>
            <button type="button" onClick={selectNone} style={{ fontSize: "12px", color: colors.slate, background: "none", border: "none", cursor: "pointer" }}>Select none</button>
          </div>
        </div>

        {tabs.map((tab) => {
          const checked = selectedKeys.has(tab.key);
          return (
            <div
              key={tab.key}
              onClick={() => toggleTab(tab.key)}
              style={{
                display: "flex", alignItems: "center", gap: "10px",
                padding: "8px 4px", borderBottom: `1px solid ${colors.border}`,
                opacity: checked ? 1 : 0.5, cursor: "pointer",
              }}
            >
              <input type="checkbox" checked={checked} onChange={() => toggleTab(tab.key)} onClick={(e) => e.stopPropagation()} />
              <span style={{ flex: 1, fontSize: "13px", fontWeight: "500" }}>{tab.label}</span>
            </div>
          );
        })}
      </div>
    </SimpleModal>
  );
}
